import { useMemo, type ReactNode } from 'react'

import { useAuthenticatedUser } from '@/legacy-src/hooks/useAuthenticatedUser'

import { AiSetupContext } from '@/src/domains/ai/contexts/AiSetup.context'
import { useGetAiPassphraseVerification } from '@/src/domains/ai/hooks/useGetAiPassphraseVerification'

import { AiSetupProviderDialog } from './AiSetupProviderDialog'
import { useAiSetupProvider } from './hooks/useAiSetupProvider'

export interface AiSetupProviderProps {
  children: ReactNode
}

export function AiSetupProvider({ children }: AiSetupProviderProps) {
  const { user } = useAuthenticatedUser()

  const { data: verification } = useGetAiPassphraseVerification()

  const {
    encryptionKey,
    recoveryKey,
    isSetupDeclined,
    isDialogOpen,
    getEncryptionKey,
    openSetupDialog,
    handleEncryptionKeySubmit,
    handleRecoveryKeySubmit,
    handleDialogOpenChange,
  } = useAiSetupProvider()

  const hasAiConsent = Boolean(user.aiConsent)

  const value = useMemo(
    () => ({
      hasAiConsent,
      verification,
      encryptionKey,
      recoveryKey,
      isSetupDeclined,
      getEncryptionKey,
      submitEncryptionKey: handleEncryptionKeySubmit,
      openSetupDialog,
    }),
    [
      hasAiConsent,
      verification,
      encryptionKey,
      recoveryKey,
      isSetupDeclined,
      getEncryptionKey,
      handleEncryptionKeySubmit,
      openSetupDialog,
    ],
  )

  return (
    <AiSetupContext.Provider value={value}>
      {children}
      {isDialogOpen ? (
        <AiSetupProviderDialog
          onEncryptionKeySubmit={handleEncryptionKeySubmit}
          onRecoveryKeySubmit={handleRecoveryKeySubmit}
          onOpenChange={handleDialogOpenChange}
        />
      ) : null}
    </AiSetupContext.Provider>
  )
}
